/**
 * PREVIEW-BUDGET-TABLE.JSX - Budget Preview Table Component with Custom Styling
 * 
 * SUMMARY:
 * Renders a responsive budget preview table for either tasks or variations, including all cost, claim, and percentage fields.
 *
 * @param {Object}   props
 * @param {string}   props.header - The table header, determines if "Tasks" or "Variations" are shown.
 * @param {Object|Array} props.tbodyData - The data to be displayed in the table body. For "Tasks", expects an object with a `tasks` array; for "Variations", expects an array.
 * @param {function} props.renderRowData - Function to render a row for each data item.
 * @param {string|number} [props.stageId] - Optional stage ID to filter variations.
 *
 * Use case: Used to display and summarize all cost, claim, and percentage data for a set of tasks or variations, including all row rendering and empty state handling.
 *
 * @returns {JSX.Element} A responsive budget preview table with fixed column widths.
 */

import { Fragment } from 'react';
import Table from 'react-bootstrap/Table';
import './preview-budget-table.scss';


const PreviewBudgetTable = ({ header, tbodyData, renderRowData, stageId }) => {
  // Render the main budget table for either Tasks or Variations
  return (
    <div className='col-12'>
      <div className='material-list-block mt-1'>
        <div className='row'>
          <div className='w-auto'>
            {/* Table heading (Tasks or Variations) */}
            <h2 className='content-heading text-uppercase mb-0'>{header}</h2>
          </div>
        </div>
        <div className='preview-budget-table'>
          <div className='table-responsive'>
            <Table
              className='table'
            >
              <thead>
                <tr>
                  {/* Table column headers - widths controlled by CSS */}
                  <th></th>
                  <th>Task</th>
                  <th>Cost</th>
                  <th>Completed %</th>
                  <th>Remaining Amount</th>
                  <th>Claim %</th>
                  <th>Claim Amount ($)</th>
                  <th>Claim Remaining</th>
                </tr>
              </thead>
              <tbody>
                {/* Render rows for Tasks */}
                {header === 'Tasks' &&
                  tbodyData &&
                  Array.isArray(tbodyData.tasks) &&
                  tbodyData.tasks.length ? (
                  tbodyData.tasks.map((task) => (
                    // Each task row is rendered using the provided renderRowData function
                    <Fragment key={task.id}>{renderRowData(task)}</Fragment>
                  ))
                ) : header !== 'Variations' ? (
                  // Show empty state if no tasks and not in Variations mode
                  <tr>
                    <td colSpan={8} className='text-center'>
                      {'No Data to Show'}
                    </td>
                  </tr>
                ) : null}

                {/* Render rows for Variations filtered by stageId */}
                {header === 'Variations' &&
                  stageId &&
                  tbodyData &&
                  Array.isArray(tbodyData) &&
                  tbodyData.map((optiontask, i) => {
                    if (optiontask.stageId !== stageId) return null; // Only show variations for this stage
                    return (
                      <Fragment key={i}>{renderRowData(optiontask)}</Fragment>
                    );
                  })}

                {/* Render rows for all approved Variations with no stageId */}
                {header === 'Variations' &&
                  !stageId &&
                  tbodyData
                    .filter((o) => o.proposal_status === 'APPROVED' && !o.stageId)
                    .map((optiontask, i) => (
                      <Fragment key={i}>{renderRowData(optiontask)}</Fragment>
                    ))}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewBudgetTable;
